import type React from 'react'
import type { JSX, MouseEvent } from 'react'
import type { IconNames, IconSizes } from 'atoms/Icon'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { COLOR } from 'vars/color'
import type { DropdownItemPadding } from '../DropdownItem.h'

import { Icon } from 'atoms/Icon'
import { TextAligning, TextBox } from 'atoms/TextBox'
import {
  DropdownItemBackgrounds,
  DropdownItemColumnGap,
  DropdownItemFontColors,
  DropdownItemFonts,
  DropdownItemPaddings,
} from '../DropdownItem.h'
import { DropdownItemTypeButtonStyled } from './DropdownItemTypeButton.styled'

export interface IDropdownItemTypeButtonProps {
  title?: ITextBoxProps['title']
  id?: number
  activeId?: number
  type?: 'submit'
  font?: DropdownItemFonts | null
  disabled?: boolean
  width?: string
  height?: number
  paddings?: DropdownItemPadding
  columnGap?: DropdownItemColumnGap
  colorDefault?: DropdownItemFontColors | COLOR
  colorHovered?: DropdownItemFontColors | COLOR
  colorPressed?: DropdownItemFontColors | COLOR
  colorSelected?: DropdownItemFontColors | COLOR
  colorDisabled?: DropdownItemFontColors | COLOR
  iconColorSelected?: DropdownItemFontColors | COLOR
  backgroundDefault?: DropdownItemBackgrounds | COLOR
  backgroundHovered?: DropdownItemBackgrounds | COLOR
  backgroundPressed?: DropdownItemBackgrounds | COLOR
  backgroundSelectedDefault?: DropdownItemBackgrounds | COLOR
  backgroundDisabledDefault?: DropdownItemBackgrounds | COLOR
  onClick?: (e: MouseEvent<HTMLElement>) => void
  children?: React.ReactNode
  selected?: boolean
  icon?: IconNames
  endIcon?: IconNames
  dataTestId?: string
  iconSize?: IconSizes
}

export const DropdownItemTypeButton = ({
  title = '',
  id,
  activeId,
  type = 'submit',
  font = DropdownItemFonts.Body4,
  disabled = false,
  width = '100%',
  height = 0,
  paddings = DropdownItemPaddings.SIZE_0,
  columnGap = DropdownItemColumnGap.INDENT_0,
  colorDefault,
  colorHovered = DropdownItemFontColors.TEXT_ICON_PRIMARY,
  colorPressed = DropdownItemFontColors.TEXT_ICON_PRIMARY,
  colorSelected = DropdownItemFontColors.TEXT_ICON_PRIMARY,
  iconColorSelected = DropdownItemFontColors.TEXT_ICON_PRIMARY,
  colorDisabled = DropdownItemFontColors.TEXT_ICON_TERTIARY,
  backgroundDefault = DropdownItemBackgrounds.TRANSPARENT,
  backgroundHovered = DropdownItemBackgrounds.SURFACE_OPACITY_LIGHT_15,
  backgroundPressed = DropdownItemBackgrounds.SURFACE_OPACITY_LIGHT_20,
  backgroundSelectedDefault = DropdownItemBackgrounds.TRANSPARENT,
  backgroundDisabledDefault = DropdownItemBackgrounds.TRANSPARENT,
  onClick,
  children,
  selected,
  icon,
  endIcon,
  dataTestId,
  iconSize,
}: IDropdownItemTypeButtonProps): JSX.Element => {
  const isSelected =
    selected ??
    ((Boolean(id) || id === 0) &&
      (Boolean(activeId) || activeId === 0) &&
      id === activeId)

  return (
    <DropdownItemTypeButtonStyled
      type={type}
      font={font}
      selected={isSelected}
      disabled={disabled}
      width={width}
      height={height}
      paddings={paddings}
      columnGap={columnGap}
      colorDefault={colorDefault}
      colorHovered={colorHovered}
      colorPressed={colorPressed}
      colorSelected={colorSelected}
      iconColorSelected={iconColorSelected}
      colorDisabled={colorDisabled}
      backgroundDefault={backgroundDefault}
      backgroundHovered={backgroundHovered}
      backgroundPressed={backgroundPressed}
      backgroundSelectedDefault={backgroundSelectedDefault}
      backgroundDisabledDefault={backgroundDisabledDefault}
      data-testid={dataTestId}
      onClick={onClick}
      role='button'
    >
      {children || (
        <>
          {icon && <Icon name={icon} size={iconSize} />}
          <TextBox title={title} alignment={TextAligning.Center} maxLines={1} />
          {endIcon && <Icon name={endIcon} />}
        </>
      )}
    </DropdownItemTypeButtonStyled>
  )
}
